/**
 * Modal de Follow-up do Lead
 */

import React, { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  IconButton,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Chip,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot
} from '@mui/lab';
import {
  CloseOutlined,
  PhoneOutlined,
  EmailOutlined,
  EventOutlined,
  NoteOutlined,
  AddOutlined
} from '@mui/icons-material';
import { Lead, FollowUpEntry, updateLeadStatusAsync, addFollowUpAsync } from '../../store/slices/groupsSlice';
import { useAppDispatch } from '../../hooks/useAppDispatch';

interface LeadFollowUpModalProps {
  open: boolean;
  onClose: () => void;
  lead: Lead;
  groupId: string;
}

const LeadFollowUpModal: React.FC<LeadFollowUpModalProps> = ({ open, onClose, lead, groupId }) => {
  const dispatch = useAppDispatch();
  const [newFollowUp, setNewFollowUp] = useState('');
  const [followUpType, setFollowUpType] = useState<string>('note');
  const [currentStatus, setCurrentStatus] = useState<string>(lead.leadStatus || 'novo');

  const getStatusLabel = (status: string) => {
    const labels: Record<string, string> = {
      'novo': 'Novo',
      'contactado': 'Contactado',
      'recusou': 'Recusou',
      'sem_contato': 'Sem Contato',
      'semContato': 'Sem Contato',
      'em_contato': 'Em Contato',
      'emContato': 'Em Contato',
      'fechado': 'Fechado'
    };
    return labels[status] || 'Novo';
  };

  const getStatusColor = (status: string) => {
    const colors: Record<string, string> = {
      'novo': '#757575',
      'contactado': '#2196f3',
      'recusou': '#f44336',
      'sem_contato': '#ff9800',
      'semContato': '#ff9800',
      'em_contato': '#9c27b0',
      'emContato': '#9c27b0',
      'fechado': '#4caf50'
    };
    return colors[status] || '#757575';
  };

  const getFollowUpIcon = (type: FollowUpEntry['type']) => {
    switch (type) {
      case 'call':
        return <PhoneOutlined />;
      case 'email':
        return <EmailOutlined />;
      case 'meeting':
        return <EventOutlined />;
      default:
        return <NoteOutlined />;
    }
  };

  const getFollowUpColor = (type: FollowUpEntry['type']) => {
    switch (type) {
      case 'call':
        return '#2196f3';
      case 'email':
        return '#9c27b0';
      case 'meeting':
        return '#4caf50';
      default:
        return '#757575';
    }
  };

  const handleStatusChange = (newStatus: string) => {
    setCurrentStatus(newStatus);
    // Atualizar status no backend
    dispatch(updateLeadStatusAsync({ leadId: lead.id, status: newStatus }));
  };

  const handleAddFollowUp = () => {
    if (newFollowUp.trim()) {
      dispatch(addFollowUpAsync({
        leadId: lead.id,
        followUp: {
          type: followUpType,
          notes: newFollowUp.trim(),
        }
      }));
      setNewFollowUp('');
      setFollowUpType('note');
    }
  };

  const handleSendWhatsApp = () => {
    // Extrair apenas números do telefone
    const phoneNumber = lead.phone?.replace(/\D/g, '') || '';
    
    if (!phoneNumber) {
      alert('Lead não possui telefone cadastrado');
      return;
    }

    // Mensagem template
    const message = `Olá! Sou da Atomic.Digital e gostaria de apresentar nossas soluções para *${lead.name}*.\n\nTemos experiência em ajudar empresas como a sua a alcançar resultados incríveis.\n\nPodemos conversar sobre como podemos ajudar?`;

    // Criar link do WhatsApp
    const whatsappUrl = `https://wa.me/${phoneNumber}?text=${encodeURIComponent(message)}`;

    // Abrir WhatsApp
    window.open(whatsappUrl, '_blank');

    // Adicionar follow-up automático
    dispatch(addFollowUpAsync({
      leadId: lead.id,
      followUp: {
        type: 'call',
        notes: `Mensagem enviada via WhatsApp: "${message}"`,
      }
    }));
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: '#1a1a1a',
          border: '1px solid rgba(255, 107, 53, 0.2)',
          maxHeight: '90vh',
        }
      }}
    >
      <DialogTitle
        sx={{
          color: '#FFFFFF',
          borderBottom: '1px solid rgba(255, 107, 53, 0.1)',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <Box>
          <Typography variant="h6">{lead.name}</Typography>
          <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
            {lead.address}
          </Typography>
        </Box>
        <IconButton onClick={onClose} sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>
          <CloseOutlined />
        </IconButton>
      </DialogTitle>

      <DialogContent 
        sx={{ 
          pt: 3,
          overflowX: 'hidden',
          '&::-webkit-scrollbar': {
            width: '8px',
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'rgba(255, 255, 255, 0.05)',
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'rgba(255, 107, 53, 0.5)',
            borderRadius: '4px',
            '&:hover': {
              backgroundColor: 'rgba(255, 107, 53, 0.7)',
            },
          },
        }}
      >
        {/* Info do Lead */}
        <Box sx={{ mb: 3, p: 2, backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 2 }}>
          <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mb: 2 }}>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Telefone
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                {lead.phone || 'Não informado'}
              </Typography>
            </Box>
            <Box>
              <Typography variant="caption" sx={{ color: 'rgba(255, 255, 255, 0.6)' }}>
                Email
              </Typography>
              <Typography variant="body2" sx={{ color: '#FFFFFF' }}>
                {lead.email || 'Não informado'}
              </Typography>
            </Box>
          </Box>

          {/* Botão WhatsApp */}
          {lead.phone && (
            <Button
              fullWidth
              variant="contained"
              onClick={handleSendWhatsApp}
              startIcon={
                <Box component="span" sx={{ fontSize: '1.2rem' }}>
                  💬
                </Box>
              }
              sx={{
                background: 'linear-gradient(135deg, #25D366 0%, #128C7E 100%)',
                color: '#FFFFFF',
                fontWeight: 600,
                py: 1.2,
                borderRadius: 2,
                textTransform: 'none',
                fontSize: '0.95rem',
                '&:hover': {
                  background: 'linear-gradient(135deg, #128C7E 0%, #25D366 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 4px 12px rgba(37, 211, 102, 0.4)',
                },
                transition: 'all 0.3s ease',
              }}
            >
              Enviar Mensagem pelo WhatsApp
            </Button>
          )}
        </Box>

        {/* Status */}
        <Box sx={{ mb: 3 }}>
          <FormControl fullWidth>
            <InputLabel sx={{ color: 'rgba(255, 255, 255, 0.7)' }}>Status</InputLabel>
            <Select
              value={currentStatus}
              label="Status"
              onChange={(e) => handleStatusChange(e.target.value)}
              sx={{
                color: '#FFFFFF',
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 255, 255, 0.2)',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: 'rgba(255, 107, 53, 0.5)',
                },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  borderColor: '#FF6B35',
                },
              }}
              renderValue={(value) => (
                <Chip
                  label={getStatusLabel(value)}
                  size="small"
                  sx={{
                    backgroundColor: `${getStatusColor(value)}20`,
                    color: getStatusColor(value),
                    fontWeight: 600,
                  }}
                />
              )}
            >
              <MenuItem value="novo">Novo</MenuItem>
              <MenuItem value="contactado">Contactado</MenuItem>
              <MenuItem value="recusou">Recusou</MenuItem>
              <MenuItem value="sem_contato">Sem Contato</MenuItem>
              <MenuItem value="em_contato">Em Contato</MenuItem>
              <MenuItem value="fechado">Negócio Fechado</MenuItem>
            </Select>
          </FormControl>
        </Box>

        <Divider sx={{ my: 3, borderColor: 'rgba(255, 107, 53, 0.1)' }} />

        {/* Add Follow-up */}
        <Box sx={{ mb: 3 }}>
          <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 2 }}>
            Adicionar Follow-up
          </Typography>
          <Box sx={{ display: 'flex', gap: 2, mb: 2 }}>
            <FormControl sx={{ minWidth: 150 }}>
              <Select
                value={followUpType}
                onChange={(e) => setFollowUpType(e.target.value as FollowUpEntry['type'])}
                size="small"
                sx={{
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                }}
              >
                <MenuItem value="note">📝 Nota</MenuItem>
                <MenuItem value="call">📞 Ligação</MenuItem>
                <MenuItem value="email">📧 E-mail</MenuItem>
                <MenuItem value="meeting">📅 Reunião</MenuItem>
              </Select>
            </FormControl>
            <TextField
              fullWidth
              size="small"
              placeholder="Descreva o follow-up..."
              value={newFollowUp}
              onChange={(e) => setNewFollowUp(e.target.value)}
              sx={{
                '& .MuiOutlinedInput-root': {
                  color: '#FFFFFF',
                  backgroundColor: 'rgba(255, 255, 255, 0.05)',
                  '& fieldset': {
                    borderColor: 'rgba(255, 255, 255, 0.2)',
                  },
                },
              }}
            />
            <Button
              variant="contained"
              onClick={handleAddFollowUp}
              disabled={!newFollowUp.trim()}
              startIcon={<AddOutlined />}
              sx={{
                background: 'linear-gradient(135deg, #FF6B35 0%, #FF8A5B 100%)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #FF8A5B 0%, #FF6B35 100%)',
                },
                '&:disabled': {
                  background: 'rgba(255, 107, 53, 0.3)',
                }
              }}
            >
              Adicionar
            </Button>
          </Box>
        </Box>

        {/* Timeline de Follow-ups */}
        <Box>
          <Typography variant="subtitle1" sx={{ color: '#FFFFFF', fontWeight: 600, mb: 3 }}>
            Histórico de Follow-up
          </Typography>

          {(!lead.followUps || lead.followUps.length === 0) ? (
            <Box sx={{ textAlign: 'center', py: 4, backgroundColor: 'rgba(255, 255, 255, 0.03)', borderRadius: 2 }}>
              <NoteOutlined sx={{ fontSize: 48, color: 'rgba(255, 255, 255, 0.3)', mb: 1 }} />
              <Typography variant="body2" sx={{ color: 'rgba(255, 255, 255, 0.5)' }}>
                Nenhum follow-up registrado ainda
              </Typography>
            </Box>
          ) : (
            <Timeline 
              sx={{ 
                p: 0, 
                m: 0,
                overflow: 'visible',
                '& .MuiTimelineItem-root': {
                  minHeight: 'auto',
                  overflow: 'visible',
                  '&:before': {
                    flex: 0,
                    padding: 0,
                  }
                },
                '& .MuiTimelineContent-root': {
                  overflow: 'visible',
                }
              }}
            >
              {(lead.followUps || []).map((entry: any, index: number) => (
                <TimelineItem key={entry.id} sx={{ mb: index < (lead.followUps || []).length - 1 ? 0 : 0 }}>
                  <TimelineSeparator sx={{ mr: 1.5 }}>
                    <TimelineDot 
                      sx={{ 
                        backgroundColor: getFollowUpColor(entry.type),
                        border: `2px solid ${getFollowUpColor(entry.type)}40`,
                        boxShadow: `0 0 8px ${getFollowUpColor(entry.type)}40`,
                        width: 40,
                        height: 40,
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        my: 0,
                        '& .MuiSvgIcon-root': {
                          fontSize: '1.2rem',
                          color: '#FFFFFF',
                        }
                      }}
                    >
                      {getFollowUpIcon(entry.type)}
                    </TimelineDot>
                    {index < (lead.followUps || []).length - 1 && (
                      <TimelineConnector 
                        sx={{ 
                          backgroundColor: 'rgba(255, 255, 255, 0.1)',
                          minHeight: 30,
                        }} 
                      />
                    )}
                  </TimelineSeparator>
                  <TimelineContent sx={{ pt: 0.5, pb: 3, pr: 0, pl: 2 }}>
                    <Box 
                      sx={{ 
                        p: 2.5, 
                        backgroundColor: 'rgba(255, 255, 255, 0.03)', 
                        borderRadius: 2,
                        border: '1px solid rgba(255, 255, 255, 0.05)',
                        transition: 'all 0.2s ease',
                        overflow: 'hidden',
                        width: '100%',
                        boxSizing: 'border-box',
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)',
                          borderColor: 'rgba(255, 107, 53, 0.3)',
                        }
                      }}
                    >
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: '#FFFFFF', 
                            lineHeight: 1.6,
                            flex: 1,
                            wordBreak: 'break-word',
                            overflowWrap: 'break-word',
                          }}
                        >
                          {entry.notes}
                        </Typography>
                      </Box>
                      <Typography 
                        variant="caption" 
                        sx={{ 
                          color: 'rgba(255, 255, 255, 0.5)',
                          display: 'flex',
                          alignItems: 'center',
                          gap: 0.5,
                        }}
                      >
                        <Box component="span" sx={{ fontSize: '0.9rem' }}>🕐</Box>
                        {new Date(entry.date).toLocaleString('pt-BR', {
                          day: '2-digit',
                          month: '2-digit',
                          year: 'numeric',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          )}
        </Box>
      </DialogContent>
    </Dialog>
  );
};

export default LeadFollowUpModal;
